"use client";
import React, { useCallback, useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { ShieldCheck, Zap, Globe, ArrowRight } from 'lucide-react';

const clampPercentage = (value) => Math.min(100, Math.max(0, value));

const ImageComparisonSlider = ({
  beforeSrc,
  afterSrc,
  beforeLabel = 'Before',
  afterLabel = 'After'
}) => {
  const containerRef = useRef(null);
  const [position, setPosition] = useState(55);
  const [isDragging, setIsDragging] = useState(false);

  const updatePosition = useCallback((clientX) => {
    if (!containerRef?.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const percent = ((clientX - rect.left) / rect.width) * 100;
    setPosition(clampPercentage(percent));
  }, []);

  const handlePointerDown = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(true);
    updatePosition(event.clientX);
  }, [updatePosition]);

  useEffect(() => {
    if (!isDragging) return undefined;

    const handlePointerMove = (event) => updatePosition(event.clientX);
    const handlePointerUp = () => setIsDragging(false);

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [isDragging, updatePosition]);

  const handleKeyDown = (event) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      setPosition((prev) => clampPercentage(prev - 5));
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      setPosition((prev) => clampPercentage(prev + 5));
    }
  };

  return (
    <div
      ref={containerRef}
      className="relative w-full aspect-[4/3] overflow-hidden cursor-ew-resize select-none bg-gray-50 rounded-[20px]"
      onPointerDown={handlePointerDown}
      style={{ touchAction: 'pan-y' }}
    >
      <Image
        src={afterSrc}
        alt={`${afterLabel} label finish`}
        fill
        sizes="(max-width: 640px) 100vw, (max-width: 1024px) 80vw, 800px"
        className="object-contain"
        priority={false}
      />
      <div
        className="absolute inset-0"
        style={{ clipPath: `polygon(0 0, ${position}% 0, ${position}% 100%, 0 100%)` }}
      >
        <Image
          src={beforeSrc}
          alt={`${beforeLabel} label finish`}
          fill
          sizes="(max-width: 640px) 100vw, (max-width: 1024px) 80vw, 800px"
          className="object-contain"
        />
      </div>
      <div className="absolute inset-0 pointer-events-none">
        <span className="absolute bottom-6 left-6 text-sm font-bold uppercase tracking-widest text-[#2E8B8D]">
          Flat Finish
        </span>
        <span className="absolute bottom-6 right-6 text-sm font-bold uppercase tracking-widest text-[#2E8B8D]">
          3D Embossed
        </span>
      </div>
      <div
        className="absolute inset-y-0"
        style={{ left: `${position}%` }}
        aria-hidden="true"
      >
        <div className="absolute inset-y-0 -translate-x-1/2 w-[1.5px] bg-white shadow-[0_0_10px_rgba(0,0,0,0.1)]" />
      </div>
      <button
        type="button"
        onPointerDown={handlePointerDown}
        onKeyDown={handleKeyDown}
        role="slider"
        aria-label="Reveal 3D embossed label comparison"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(position)}
        className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 flex items-center justify-center w-[50px] h-[50px] rounded-full bg-[#1a1a1a] border-[2.5px] border-white shadow-[0px_4px_12px_rgba(0,0,0,0.3)] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-global-3 active:scale-95 transition-transform"
        style={{ left: `${position}%` }}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M8 16L4 12L8 8" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M16 8L20 12L16 16" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
    </div>
  );
};

const WhyChooseSection = () => {
  const whyChooseFeatures = [
    {
      icon: ShieldCheck,
      title: '100% In-House Production',
      description: 'Complete control over quality and timelines'
    },
    {
      icon: Zap,
      title: 'Quick Turnarounds',
      description: 'Fast delivery without compromising quality'
    },
    {
      icon: Globe,
      title: 'Trusted Across 10+ Industries',
      description: 'Proven expertise in diverse sectors'
    }
  ];

  const featuredCards = [
    {
      badge: 'Featured',
      badgeColor: 'bg-global-3',
      title: '8–10 Color \nFlexo Machines',
      description: 'Advanced printing technology for vibrant results'
    },
    {
      badge: 'Popular',
      badgeColor: 'bg-global-6',
      title: 'Labelling for \nAny Substrate',
      description: 'Versatile solutions for all material types'
    },
    {
      badge: 'Latest',
      badgeColor: 'bg-global-5',
      title: 'Custom Foiling, \nUV, Die Cuts',
      description: 'Premium finishing options available'
    }
  ];

  return (
    <section className="section-shell w-full relative overflow-hidden !pt-[40px]">
      <div className="container-fluid">
        <div className="flex flex-col xl:flex-row justify-center items-center gap-fluid-xl">
          {/* Left Content */}
          <div className="w-full xl:w-[44%] flex flex-col gap-[clamp(1.25rem,3vw,1.875rem)] items-center xl:items-start">
            <div className="relative w-full">
              <h2 className="type-section font-bold text-center xl:text-left text-global-2 whitespace-pre-line leading-tight">
                Why Choose<br />Royal Prime?
              </h2>
            </div>
            <div className="flex flex-col gap-[clamp(1rem,2vw,1.25rem)] w-full">
              {whyChooseFeatures?.map((feature, index) => (
                <div key={index} className="flex gap-[clamp(0.75rem,2vw,1.25rem)] items-center w-full">
                  <div className="w-[56px] h-[56px] rounded-[16px] bg-[#FDFBF7] border border-[#F5D080]/30 flex items-center justify-center text-[#C9A84C] flex-shrink-0">
                    <feature.icon className="w-[30px] h-[30px]" />
                  </div>
                  <div className="flex flex-col items-center xl:items-start flex-1 font-sans">
                    <p className="text-base sm:text-lg font-bold text-center xl:text-left text-global-7">
                      {feature?.title}
                    </p>
                    <p className="text-sm sm:text-base text-center xl:text-left text-global-8">
                      {feature?.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
            <Link href="/about" className="no-tap-enforce flex items-center gap-[16px] pt-[16px] mt-8 w-full group justify-center xl:justify-start">
              <span className="text-xl sm:text-2xl font-bold text-left text-global-11 group-hover:text-global-9 transition-colors duration-200 font-sans">
                Get to Know More About Us
              </span>
              <ArrowRight className="w-[24px] h-[24px] text-global-11 transition-transform duration-300 group-hover:translate-x-1 flex-shrink-0" />
            </Link>
          </div>
          {/* Right Content */}
          <div className="w-full xl:w-[50%] relative min-w-0">
            <img
              src="/images/img_blob.svg"
              alt=""
              className="absolute top-0 right-0 w-[300px] h-[300px] sm:w-[500px] sm:h-[500px] lg:w-[690px] lg:h-[622px] z-0 opacity-20"
            />
            <div className="relative z-10 flex flex-col items-center gap-[40px]">
              {/* Comparison Slider */}
              <div className="relative w-full max-w-[800px] overflow-hidden">
                <ImageComparisonSlider
                  beforeSrc="/images/simple-3.webp"
                  afterSrc="/images/Effect-3-.webp"
                  beforeLabel="Flat Finish"
                  afterLabel="3D Embossed"
                />
              </div>
              {/* Feature Cards — 1 col → 2 col (sm) → 3 col (md+) */}
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-[12px] w-full max-w-[800px]">
                {featuredCards?.map((card, index) => (
                  <div key={index} className="bg-global-9 rounded-[10px] shadow-[0px_0px_10px_#00000011] p-[16px] flex flex-col justify-between w-full">
                    <div className="flex flex-col gap-[12px]">
                      <h3 className="type-block text-left text-global-2 whitespace-pre-line font-bold">
                        {card?.title}
                      </h3>
                      <p className="type-body text-left text-global-8 text-sm">
                        {card?.description}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseSection;